/**
 * WriteSlideML LLM Reference — Comprehensive guide for the readSlide/writeSlide workflow.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * MAINTENANCE NOTE
 *
 * This file is injected into the orchestrator system prompt. Any changes to:
 *   - ReadSlideML XML format (slide_xml_formatter.py)
 *   - WriteSlideML executor capabilities (com/WriteSlideML/executor.py)
 *   - Def registry (DefRegistry in slide_xml_formatter.py)
 *   - Shape extractor output (aspose_modules/slide_reader/)
 *
 * ...MUST be reflected here, or the LLM will produce incorrect XML.
 *
 * See also:
 *   - readSlideML.schema.ts          — Full TypeScript type definitions
 *   - com/WriteSlideML/executor.py   — Canonical writable operation support
 * ═══════════════════════════════════════════════════════════════════════════════
 */

export const WRITE_SLIDE_ML_REFERENCE = `
## ReadSlideML / WriteSlideML Complete Reference

Slides are read and edited through **ReadSlideML**, a custom XML format.
- **readSlide** returns the slide as ReadSlideML XML
- **writeSlide** accepts search/replace blocks on that XML, the backend diffs and executes via COM
- All coordinates are in **points** (1pt = 1/72 inch). Standard slide: **720 x 540** pts.

---

### XML Document Structure

\`\`\`xml
<slide schema="readslideml-v1" detail="complete" id="slide_318" index="10"
       layoutName="Title and Content" layoutId="-1"
       masterName="Template_English" w="720" h="540">
  <defs> ... </defs>          <!-- Shared style defs — DO NOT edit -->
  <layout> ... </layout>      <!-- Master/layout shapes — DO NOT edit -->
  <content> ... </content>    <!-- Slide shapes — YOUR EDIT TARGETS -->
  <offSlide> ... </offSlide>  <!-- Off-canvas shapes — read-only -->
  <fyi> ... </fyi>            <!-- Whitespace/overlaps — read-only, DO NOT include in changes -->
</slide>
\`\`\`

---

### Def References (Style System)

TextFrames can use \`ref\` too: \`<textFrame ref="tf1"/>\`. Inline attrs override the ref
(\`<textFrame ref="tf1" marginLeft="7.2"/>\`).

Shared styles in \`<defs>\` are referenced via \`ref="..."\` (fills/outlines/fonts/textFrames/tableBorders)
and via \`style="pX"\` on \`<p>\` tags (paragraph styles). IDs are **stable** between read/write.

\`\`\`xml
<defs>
  <!-- Fonts: fn1, fn2, ... -->
  <font id="fn1" name="Arial" sizePt="10" color="#000000"/>
  <font id="fn2" name="Arial" sizePt="11" bold="1" color="#55CEFF"/>

  <!-- Paragraph styles: p1, p2, ... -->
  <paraStyle id="p1" alignment="left" level="0" bulletType="none"/>

  <!-- Fills: f1, f2, ... -->
  <fill id="f1" type="solid" color="#C6EFFF"/>

  <!-- Outlines: o1, o2, ... -->
  <outline id="o1" color="#A9B1B0" width="0.75"/>

  <!-- Table cell borders: tb1, tb2, ... -->
  <tableBorder id="tb1" visible="1" color="#FFFFFF" width="1" transparency="0.15"/>

  <!-- Text frames: tf1, tf2, ... -->
  <textFrame id="tf1" marginLeft="0" marginTop="3.6" marginRight="0"
             marginBottom="0" verticalAnchor="top" autofit="shrink" wordWrap="1"/>
</defs>
\`\`\`

You can use \`ref="f1"\` OR write inline values — both work.
Paragraphs use \`style="p1"\` to reference a \`paraStyle\`.

**Creating new defs:** You CAN add new entries to \`<defs>\`:
\`\`\`xml
<font id="fn_custom1" name="Calibri" sizePt="14" bold="1" color="#FF0000"/>
<fill id="f_custom1" type="solid" color="#2D8F78"/>
<tableBorder id="tb_custom1" visible="1" color="#D9D9D9" width="0.75" transparency="0.2"/>
\`\`\`
Then reference them: \`<run font="fn_custom1">\` or \`<fill ref="f_custom1"/>\`.
Use descriptive IDs (e.g., \`fn_custom1\`, \`f_accent\`) to avoid collisions with existing defs.
Editing existing defs is supported when you intentionally want global ref-based updates.
This includes \`font\`, \`paraStyle\`, \`fill\`, \`outline\`, \`effect\`, table \`tableBorder\`,
and referenced \`textFrame\` defs.

---

### Fill [EDIT]

| State | XML |
|-------|-----|
| No fill | \`<fill/>\` |
| Solid (ref) | \`<fill ref="f1"/>\` |
| Solid (inline) | \`<fill type="solid" color="#FF0000"/>\` |
| Solid + transparency | \`<fill type="solid" color="#FF0000" transparency="0.5"/>\` |
| Gradient | \`<fill type="gradient" gradientType="linear" gradientAngle="90"><gradientStops><stop position="0" color="#FF0000"/><stop position="1" color="#0000FF"/></gradientStops></fill>\` |
| Pattern | \`<fill type="pattern" patternType="dkHorz" foregroundColor="#000000" backgroundColor="#FFFFFF"/>\` |

Preferred no-fill form is \`<fill/>\`. Runtime also accepts \`<fill type="none"/>\` and
\`<fill visible="0"/>\`, but use \`<fill/>\` for canonical output and best round-trip stability.

Fill types: \`solid\`, \`gradient\`, \`pattern\`, \`picture\`, \`group\`
Gradient types: \`linear\`, \`radial\`, \`rectangular\`, \`path\`

---

### Outline [EDIT]

| State | XML |
|-------|-----|
| No outline | \`<outline/>\` |
| Outline (ref) | \`<outline ref="o1"/>\` |
| Outline (inline) | \`<outline color="#C6EFFF" width="1.5"/>\` |
| Full (complete detail) | \`<outline color="#000000" width="2" dashStyle="dash" transparency="0.35" capStyle="round" joinStyle="miter"/>\` |

Preferred no-outline form is \`<outline/>\`. Runtime also accepts \`<outline visible="0"/>\`,
but use \`<outline/>\` for canonical output and best round-trip stability.

Dash styles: \`solid\`, \`dash\`, \`dot\`, \`dashDot\`, \`longDash\`, \`longDashDot\`, \`dashDotDot\`, \`roundDot\`, \`squareDot\`
Cap styles: \`flat\`, \`round\`, \`square\`
Join styles: \`bevel\`, \`miter\`, \`round\`

---

### Shape Block [EDIT where marked]

\`\`\`xml
<shape shapeId="5" name="Rectangle 4" type="shape" zIndex="3" from="slide"
       rotation="45" hidden="1">                                <!-- [EDIT] rotation + hidden -->
  <position x="115.87" y="145.42" width="240" height="150"/>  <!-- [EDIT] pts -->
  <fill ref="f1"/>                                              <!-- [EDIT] -->
  <outline/>                                                    <!-- [EDIT] incl dashStyle -->
  <textFrame marginLeft="0" marginTop="3.6" marginRight="0" marginBottom="3.6"
             wordWrap="1" verticalAnchor="top" autofit="shrink" textDirection="horizontal"/>  <!-- [EDIT] -->
  <p style="p1">                                                  <!-- [EDIT] style ref -->
    <run font="fn1"><![CDATA[Hello World]]></run>                 <!-- [EDIT] text + font -->
  </p>
  <textFrameDimensions x="40.1" y="62.17" width="639.79" height="19.28"/>  <!-- [RO] always -->
  <actualTextBounds x="40.99" y="62.17" width="187.88" height="19.46"/>    <!-- [RO] always -->
  <geometry presetGeometry="RECTANGLE">                         <!-- [EDIT] presetGeometry + adjustments -->
    <adjustments adj1="0.16667"/>                               <!-- [EDIT] adjustments -->
  </geometry>
  <effects>                                                     <!-- [EDIT] shadow -->
    <shadow type="outer" color="#000000" blurRadius="5"
            distance="3" angle="315" transparency="0.5"/>
  </effects>
  <placeholder type="content" index="0" size="full"/>           <!-- [RO] -->
</shape>
\`\`\`

Shape types: \`shape\`, \`textBox\`, \`placeholder\`, \`table\`, \`chart\`, \`picture\`, \`connector\`, \`group\`, \`smartArt\`, \`media\`, \`oleObject\`
Create routing for new shapes:
- \`type="textBox"\` (or \`presetGeometry="TEXTBOX"\` / \`"TEXT_BOX"\`) creates a true textbox (COM \`AddTextbox\`)
- \`type="shape"\` with \`presetGeometry="RECTANGLE"\` creates a rectangle shape (COM \`AddShape\`)
- If \`type="shape"\` omits \`presetGeometry\`, it defaults to rectangle shape creation (\`AddShape\`)
Shape origins: \`from="slide"\` (editable), \`from="master"\` / \`from="layout"\` (editable but affects all slides using same master/layout)

Editable shape attrs: \`rotation="45"\` (degrees), \`hidden="1"\` (toggle visibility)
Read-only shape attrs: \`shapeId\`, \`type\`, \`from\`, \`inherited\`, \`zIndex\` (use z-order tools), \`description\`

---

### TextFrame [EDIT]

\`\`\`xml
<textFrame marginLeft="7.2" marginTop="3.6" marginRight="7.2" marginBottom="3.6"
           wordWrap="1" verticalAnchor="middle" autofit="none"/>
\`\`\`

Vertical anchor: \`top\`, \`middle\`, \`bottom\`
Text direction: \`horizontal\`, \`vertical\`, \`vertical270\`, \`stacked\`
Autofit: \`none\`, \`normal\` (resize shape to fit), \`shrink\` (shrink text on overflow)
WordWrap: \`1\` (on) or omit/\`0\` (off)
Optional: \`columns="2" columnSpacing="18"\`

Create-time inset defaults (important):
- True textbox create (\`type="textBox"\`) defaults missing textFrame margins to \`0\` (left/top/right/bottom)
- Rectangle shape create (\`type="shape"\` + \`presetGeometry="RECTANGLE"\`) uses PowerPoint rectangle defaults
- To force deterministic spacing, set all four margins explicitly or use \`<textFrame ref="tfX"/>\`

Resize behavior with \`autofit="normal"\`:
- During position+size edits, runtime temporarily guards against COM auto-resize pushback.
- If you are shrinking height and want stable geometry, prefer \`autofit="shrink"\` or \`autofit="none"\`.

---

### Paragraphs & Runs [EDIT]

\`\`\`xml
<!-- Simple paragraph (style ref) -->
<p style="p1">
  <run font="fn1"><![CDATA[Hello World]]></run>
</p>

<!-- Paragraph with bullet (style ref) -->
<paraStyle id="p_bullet" alignment="left" level="0" indent="-13.5" marginLeft="13.5"
           bulletType="char" bulletChar="&#x2022;"/>
<p style="p_bullet">
  <run font="fn2"><![CDATA[Bullet item]]></run>
</p>

<!-- Multi-run paragraph (different formatting per run) -->
<p style="p1">
  <run font="fn1"><![CDATA[Normal text ]]></run>
  <run font="fn3"><![CDATA[bold text]]></run>
</p>

<!-- Run with inline font overrides (bold + color override) -->
<p style="p1">
  <run font="fn1" bold="1" color="#FF0000"><![CDATA[Highlighted text]]></run>
</p>
\`\`\`

**Paragraph attributes [EDIT]:** style (paraStyle ref), alignment, level, spaceBefore, spaceAfter, lineSpacing, indent, marginLeft, marginRight, defaultTabSize
**Bullet attributes [EDIT]:** bulletType, bulletChar, bulletColor, bulletSize, bulletFont, numberFormat, startAt
**Run text [EDIT]:** CDATA content
**Run font [EDIT]:** Change font ref OR add inline overrides (bold, italic, color, sizePt, etc.)
**Inline overrides:** You can include paragraph attrs on \`<p>\` even with \`style="pX"\`. Inline attrs override the referenced \`paraStyle\`.

Alignment: \`left\`, \`center\`, \`right\`, \`justify\`, \`distributed\`
Bullet types: \`char\`, \`autoNumber\`, \`picture\`
Number formats (for autoNumber): \`arabicPeriod\` (1. 2. 3.), \`arabicParenR\` (1) 2) 3)), \`arabicParenBoth\` ((1) (2) (3)), \`arabicPlain\` (1 2 3), \`romanUcPeriod\` (I. II. III.), \`romanLcPeriod\` (i. ii. iii.), \`alphaUcPeriod\` (A. B. C.), \`alphaLcPeriod\` (a. b. c.), \`alphaLcParenR\` (a) b) c)), \`alphaUcParenR\` (A) B) C))
Start at: \`startAt="1"\` (default), \`startAt="3"\` (start numbering at 3)
Spacing: \`spaceBefore="6"\`, \`spaceAfter="6"\`, \`lineSpacing="1.2"\`

#### Hanging Indent Pattern (CRITICAL for proper bullets)

For a bullet to render correctly with text wrapping, \`marginLeft\` and \`indent\` must be equal and opposite:

\`\`\`xml
<!-- Correct bullet with hanging indent: bullet at margin, text wraps at marginLeft -->
<p level="0" alignment="left" marginLeft="13.5" indent="-13.5"
   bulletType="char" bulletChar="&#x2022;">
  <run font="fn1"><![CDATA[This is a long bullet that wraps properly because the hanging indent pulls the bullet left while the text body starts at marginLeft.]]></run>
</p>

<!-- Numbered bullet with hanging indent -->
<p level="0" alignment="left" marginLeft="18" indent="-18"
   bulletType="autoNumber" numberFormat="arabicPeriod" startAt="1">
  <run font="fn1"><![CDATA[First numbered item]]></run>
</p>

<!-- Multi-level bullets -->
<p level="0" alignment="left" marginLeft="13.5" indent="-13.5"
   bulletType="char" bulletChar="&#x2022;">
  <run font="fn1"><![CDATA[First level]]></run>
</p>
<p level="1" alignment="left" marginLeft="27" indent="-13.5"
   bulletType="char" bulletChar="&#x2013;">
  <run font="fn1"><![CDATA[Second level (indented)]]></run>
</p>
\`\`\`

**The rule:** \`marginLeft\` = left edge of text block, \`indent\` = first line offset (negative = hanging).
The bullet sits at \`marginLeft + indent\` (= 0 for level 0), and text wraps at \`marginLeft\`.
| Level | marginLeft | indent | Effect |
|-------|-----------|--------|--------|
| 0 | 13.5 | -13.5 | First-level bullet |
| 1 | 27.0 | -13.5 | Second-level, indented |
| 2 | 40.5 | -13.5 | Third-level |

---

### Fonts [EDIT via inline run overrides or def edits]

You can change runs via \`font="fnX"\` refs or inline overrides, and you can also edit existing
\`<font id="fnX".../>\` defs when you intentionally want global ref-based updates.
For targeted changes, inline run overrides remain the safest default:

\`\`\`xml
<!-- Switch to a different font def -->
<run font="fn2"><![CDATA[Now using fn2 style]]></run>

<!-- Keep font ref but override specific properties inline -->
<run font="fn1" bold="1" color="#FF0000" sizePt="14"><![CDATA[Bold red 14pt]]></run>

<!-- Add highlight color -->
<run font="fn1" highlightColor="#FFFF00"><![CDATA[Highlighted text]]></run>
\`\`\`

Inline overrides take precedence over the font def's properties.

**Editable font properties** (as inline attrs on \`<run>\`):
- \`name\` — font family name
- \`sizePt\` — point size
- \`bold\` — \`"1"\` or omit
- \`italic\` — \`"1"\` or omit
- \`color\` — \`"#RRGGBB"\`
- \`highlightColor\` — \`"#RRGGBB"\` text highlight/background
- \`underline\` — \`single\`, \`double\`, \`heavy\`, \`dotted\`, \`dashed\`, \`wavy\`
- \`strikethrough\` — \`single\`, \`double\`
- \`superscript\` — \`"1"\` or omit
- \`subscript\` — \`"1"\` or omit
- \`spacing\` — character spacing in points (can be negative)

---

### Hyperlinks [EDIT]

\`\`\`xml
<!-- Add external URL hyperlink (add <link/> tag after the run) -->
<run font="fn1"><![CDATA[Click here]]></run>
<link url="https://example.com" tooltip="Visit example"/>

<!-- Remove hyperlink: simply remove the <link/> tag -->
<run font="fn1"><![CDATA[Click here]]></run>
\`\`\`

To add a hyperlink: place a \`<link url="..." tooltip="..."/>\` tag after the \`</run>\` closing tag.
To remove: delete the \`<link/>\` tag. Internal slide links (\`slide:N\`) are read-only for now.

---

### Images [EDIT position with aspect lock, EDIT image via contextId, EDIT crop]

\`\`\`xml
<shape shapeId="10" name="Picture 2" type="picture" zIndex="7"
       description="Company Logo" from="slide">
  <position x="199.87" y="147.7" width="72" height="37.63"/>
  <fill/>
  <outline/>
  <imageInfo originalWidth="1200" originalHeight="627">
    <crop left="0.1" right="0" top="0" bottom="0.05"/>
  </imageInfo>
</shape>
\`\`\`

**Aspect ratio lock:** When resizing picture shapes (\`type="picture"\` or image fill), aspect ratio is
automatically enforced. If both width and height change, the smaller scale factor wins. If only one
dimension is specified, the other is computed from the aspect ratio.

**Change image content:** Set the fill to \`type="picture"\` with a \`contextId\` referencing a stored image:
\`\`\`xml
<fill type="picture" contextId="logo_abc123"/>
\`\`\`
Valid contextId prefixes: \`logo_*\` (Brandfetch), \`wikimedia_*\` (Wikimedia Commons), \`artifact_*\` (Excel artifacts).
The image data is automatically resolved from the ContextResolver and sent to the backend.

**Image crop [EDIT]:** Fractional values (0–1) controlling how much of the original image is cropped
from each edge. \`0.1\` = 10% cropped. Omit the \`<crop/>\` tag or set all values to 0 to remove cropping.
\`\`\`xml
<crop left="0.1" right="0" top="0" bottom="0.05"/>
\`\`\`

Complete detail also adds: \`contentType="image/png"\`, \`brightness="0"\`, \`contrast="0"\`

---

### Excel Artifacts [CREATE/REPLACE via writeSlide, image or OLE]

Use Excel artifact IDs (\`artifact_*\`) produced by linked Excel tooling to insert chart/range content directly in writeSlide.

\`\`\`xml
<shape shapeId="new_1" name="Revenue Artifact" type="excelArtifact">
  <position x="80" y="110" width="560" height="300"/>
  <excelArtifact contextId="artifact_abc123" mode="image"/>
</shape>
\`\`\`

**Modes:**
- \`mode="image"\` → static visual insertion (default if mode omitted)
- \`mode="ole"\` → editable embedded Excel object (Windows/COM path)

**Required fields:**
- \`contextId\` must be an existing \`artifact_*\` entry in ContextResolver/global session context.
- \`position\` is required. If one dimension is omitted, backend uses artifact aspect ratio when available.

**Automatic metadata behavior:**
- writeSlide auto-attaches artifact metadata using \`sourceContextId=artifact_*\`.
- \`insertionMode\` is tracked (\`image\` or \`ole\`).
- You can still explicitly edit \`<carouselMetadata>\`; payload is read-only in XML and resolved at runtime.

**Replace existing shape with artifact:**
- Edit an existing shape block and add/change the \`<excelArtifact .../>\` child.
- Backend replaces the shape at that position and updates metadata to the new inserted shape ID.

---

### Tables [EDIT]

\`\`\`xml
<shape shapeId="20" name="Table 1" type="table" zIndex="5" from="slide">
  <position x="40" y="100" width="640" height="300"/>
  <fill/>
  <outline/>
  <tableInfo rows="3" columns="4">
    <columnWidths>
      <columnWidth v="160"/>       <!-- [EDIT] change column widths -->
      <columnWidth v="160"/>
      <columnWidth v="160"/>
      <columnWidth v="160"/>
    </columnWidths>
    <rowHeights>
      <rowHeight v="30"/>          <!-- [EDIT] change row heights -->
      <rowHeight v="30"/>
      <rowHeight v="30"/>
    </rowHeights>
    <cells>
      <cell row="1" column="1" rowSpan="1" colSpan="1">
        <fill type="solid" color="#005AA4"/>     <!-- [EDIT] cell fill -->
        <verticalAlign v="middle"/>               <!-- [EDIT] cell vert align -->
        <textDirection v="horizontal"/>           <!-- [EDIT] cell text direction -->
        <borders>                                  <!-- [EDIT] cell borders -->
          <top ref="tb1"/>
          <bottom ref="tb1"/>
          <left ref="tb1"/>
          <right ref="tb1"/>
        </borders>
        <margins left="5" right="5" top="3" bottom="3"/>  <!-- [EDIT] cell margins -->
        <textFrame ref="tf1" marginLeft="5" marginRight="5" marginTop="3" marginBottom="3"
                   wordWrap="1" autofit="shrink" verticalAnchor="middle"/>  <!-- [EDIT] cell textFrame (optional; overrides margins/verticalAlign) -->
        <paragraphs>
          <p level="0" alignment="center">
            <run font="fn2"><![CDATA[Header]]></run>        <!-- [EDIT] cell text -->
          </p>
        </paragraphs>
      </cell>
      <!-- ... more cells ... -->
    </cells>
  </tableInfo>
</shape>
\`\`\`

**Editable:** cell text (CDATA), cell fill (including \`transparency\`), cell borders (top/bottom/left/right, including \`transparency\`), cell margins,
cell vertical align, cell text direction, **cell textFrame** (margins/wordWrap/autofit/verticalAnchor, supports \`ref\`),
column widths, row heights.
Cell \`<p>\` tags support \`style="pX"\` and cell \`<run>\` tags support \`font="fnX"\` just like regular shapes.
Compatibility: cell \`verticalAlign\` and \`textDirection\` are accepted both as \`<cell ...>\` attrs and child tags
(\`<verticalAlign v="..."/>\`, \`<textDirection ...>\`). If no paragraph/run content exists, \`text="..."\` on \`<cell>\`
is also accepted as text fallback.
Precedence: cell \`<textFrame ... verticalAnchor="..."/>\` overrides cell \`verticalAlign\`.
**Structural:** add/remove rows (add/remove \`<rowHeight>\` entries + cells), add/remove columns (add/remove \`<columnWidth>\` entries + cells).
**Also editable:** cell merge layout via \`rowSpan\`/\`colSpan\`, table style flags (\`firstRow\`, \`lastRow\`,
\`firstColumn\`, \`lastColumn\`, \`bandedRows\`, \`bandedColumns\`), and diagonal borders
(\`diagonalUp\`, \`diagonalDown\`).

**IMPORTANT (table style inheritance):**
- If \`tableStyleId\` is present and you are adding rows/columns, prefer inheriting borders from table style.
- Do **not** add explicit \`<borders>\` blocks to new cells unless the user explicitly asks for custom border styling.
- Explicit per-cell borders can override style inheritance and render differently from neighboring style-driven cells.
- For style-consistent inserts: add new \`<cell>\` blocks without \`<borders>\` and keep \`tableStyleId\` unchanged.

Cell vertical align: \`top\`, \`middle\`, \`bottom\`
Cell text direction: \`horizontal\`, \`vertical\`, \`vertical270\`, \`stacked\`
Border properties: \`visible\` ("0"/"1"), \`color\` (#RRGGBB), \`width\` (pts), \`dashStyle\`, \`transparency\` (0–1)
Border refs: use \`<top ref="tbX"/>\` / \`<bottom ref="tbX"/>\` / \`<left ref="tbX"/>\` / \`<right ref="tbX"/>\`
to reuse shared \`<tableBorder>\` defs.
Diagonal edges use \`<diagonalDown .../>\` and \`<diagonalUp .../>\` (inline attrs or \`ref="tbX"\`).
Border dash styles: \`solid\`, \`dash\`, \`dot\`, \`dashDot\`, \`dashDotDot\`
Hide border: \`<top visible="0"/>\`
Editing a referenced \`<tableBorder id="tbX".../>\` or \`<textFrame id="tfX".../>\` updates all table cells
that reference \`tbX\` / \`tfX\`.

---

### Connectors [EDIT points, line, arrowheads]

\`\`\`xml
<shape shapeId="112" name="Line - Top" type="connector" zIndex="5" from="slide">
  <position x="39.32" y="58.85" width="641.17" height="0"/>
  <fill ref="f2"/>
  <outline ref="o1"/>
  <connectorInfo connectorType="straight">
    <points>                                     <!-- [EDIT] move/add/remove points -->
      <point x="39.32" y="58.85"/>
      <point x="680.49" y="58.85"/>
    </points>
    <line color="#A9B1B0" width="0.75"/>          <!-- [EDIT] line styling -->
    <arrowheadEnd style="triangle" length="medium" width="medium"/>  <!-- [EDIT] -->
  </connectorInfo>
</shape>
\`\`\`

**Editable:** points (absolute slide coordinates — add, move, or remove), line styling (color, width/weight, dashStyle, transparency, including \`<line ref="oX"/>\`), arrowheads (start/end: style, length, width)
**Read-only:** connectorType (cannot morph type — delete and recreate), startConnection/endConnection (informational)

Points are the actual through-points the connector passes through (not abstract adjustment values).
To add a waypoint to an elbow connector, add a \`<point>\` between existing points.
To straighten a line, keep only the first and last \`<point>\`.

Connector types: \`straight\`, \`elbow\`, \`curved\`
Line properties: \`color\` (#RRGGBB), \`width\`/\`weight\` (pts), \`dashStyle\`, \`transparency\`
Arrowhead styles: \`none\`, \`triangle\`, \`open\`, \`stealth\`, \`diamond\`, \`oval\`
Arrowhead length: \`short\`, \`medium\`, \`long\`
Arrowhead width: \`narrow\`, \`medium\`, \`wide\`

---

### Geometry [presetGeometry EDIT, adjustments EDIT]

\`\`\`xml
<geometry presetGeometry="RECTANGLE"/>
<geometry presetGeometry="ROUNDED_RECTANGLE">
  <adjustments adj1="0.16667"/>   <!-- [EDIT] change corner radius etc. -->
</geometry>
\`\`\`

\`presetGeometry\` is **editable** — change to morph a shape's outline (e.g. rectangle → ellipse).
The shape retains its size, fill, outline, text, and other attributes. Adjustment values reset to
defaults when the geometry type changes.

\`adjustments\` are **editable** — float values that control shape-specific parameters:
- ROUNDED_RECTANGLE: adj1 = corner radius (0–1)
- RIGHT_ARROW: adj1 = head width, adj2 = head length
- Most shapes: 0–2 adjustments

Common presets: \`RECTANGLE\`, \`ROUNDED_RECTANGLE\`, \`ELLIPSE\`, \`TRIANGLE\`, \`RIGHT_ARROW\`, \`LINE\`, \`NOT_DEFINED\`

---

### Effects [shadow EDIT, reflection/glow/softEdge RO]

\`\`\`xml
<!-- Add shadow -->
<effects>
  <shadow type="outer" color="#000000" blurRadius="5" distance="3" angle="315"
          transparency="0.5"/>
</effects>

<!-- Remove shadow: remove the entire <effects> block -->
\`\`\`

**Shadow [EDIT]:** type (\`outer\`/\`inner\`), color (#RRGGBB), blurRadius (pts), distance (pts), angle (degrees), transparency (0–1), scaleX, scaleY
**Reflection [RO]**, **Glow [RO]**, **Soft Edge [RO]** — read but cannot modify via COM

---

### Placeholder [RO]

\`\`\`xml
<placeholder type="content" index="0" size="full"/>
<placeholder type="title" index="0"/>
\`\`\`

Types: \`title\`, \`body\`, \`centeredTitle\`, \`subtitle\`, \`date\`, \`slideNumber\`, \`footer\`, \`header\`, \`object\`, \`chart\`, \`table\`, \`clipArt\`, \`smartArt\`, \`media\`, \`picture\`, \`content\`

---

### Metadata [EDIT attach/detach/update mapsTo, RO payload]

\`\`\`xml
<carouselMetadata>
  <entries>
    <entry type="citation" sourceContextId="as_abc123">
      <mapsTo>
        <mapTo row="2" col="1">
          <text>
            <item><![CDATA[market grew 37%]]></item>
            <item><![CDATA[YoY growth]]></item>
          </text>
        </mapTo>
      </mapsTo>
      <payload>
        <documentTitle v="Q3 Earnings Call"/>
        <source v="alphasense"/>
      </payload>
    </entry>
  </entries>
</carouselMetadata>
\`\`\`

**Attach metadata:** Add an \`<entry>\` with \`type\` and \`sourceContextId\` to the shape's \`<carouselMetadata>\` block.
**Detach metadata:** Remove the \`<entry>\` from the block (or remove the entire \`<carouselMetadata>\` section).
**Update targeting:** Keep the same \`sourceContextId\` and edit \`<mapsTo>\` (or remove it to clear targeting).
**Payload is read-only** — the backend resolves payload from the sourceContextId. Don't include payload in writes.

**mapsTo format:**
- \`mapsTo\` is a list of \`mapTo\` targets
- each \`mapTo\` may include \`row\` and \`col\` (**1-indexed**)
- each \`mapTo\` may include text snippets under \`<text><item>...</item></text>\`
- use \`row/col\` for table-cell targeting; use \`text\` for shape-level text targeting

\`\`\`xml
<!-- To attach a citation to a shape, add carouselMetadata: -->
<carouselMetadata>
  <entries>
    <entry type="citation" sourceContextId="as_abc123">
      <mapsTo>
        <mapTo>
          <text>
            <item><![CDATA[market grew 37%]]></item>
          </text>
        </mapTo>
      </mapsTo>
    </entry>
  </entries>
</carouselMetadata>
\`\`\`

Citation URLs are stripped from XML to save tokens (resolved at runtime via sourceContextId).

---

### Charts [EDIT styling, RO data values in v1]

\`\`\`xml
<shape shapeId="30" name="Chart 1" type="chart" zIndex="5" from="slide">
  <position x="50" y="100" width="620" height="350"/>
  <fill/>
  <outline/>
  <chartInfo chartType="columnClustered" dataMode="raw" seriesCount="3">
    <title show="1" position="above">
      <text><![CDATA[Revenue by Region]]></text>
      <textFormat fontFamily="Arial" fontSize="14" bold="1" color="#000000"/>
      <layout x="0.3" y="0.02" width="0.4" height="0.08"/>  <!-- [EDIT] fractional 0-1 -->
    </title>

    <legend show="1" position="bottom" overlay="0">
      <textFormat fontFamily="Arial" fontSize="9" color="#333333"/>
      <layout x="0.2" y="0.9" width="0.6" height="0.08"/>   <!-- [EDIT] fractional 0-1 -->
    </legend>

    <categories>
      <value v="Q1"/><value v="Q2"/><value v="Q3"/><value v="Q4"/>
    </categories>

    <series index="0" mode="raw" name="Americas">
      <values><value v="120"/><value v="135"/><value v="150"/><value v="142"/></values>
      <fill type="solid" color="#4472C4"/>
      <outline/>
      <dataLabels show="1" position="outsideEnd" showValue="1" formatCode="#,##0">
        <textFormat fontFamily="Arial" fontSize="8" color="#000000"/>
      </dataLabels>
    </series>

    <series index="1" mode="raw" name="EMEA">
      <values><value v="80"/><value v="95"/><value v="88"/><value v="102"/></values>
      <fill type="solid" color="#ED7D31"/>
      <outline/>
    </series>

    <categoryAxis visible="1">
      <title show="0"/>
      <majorGridlines show="0"/>
      <majorTickMark v="outside"/>
      <tickLabels show="1" formatCode="General" position="nextTo">
        <textFormat fontFamily="Arial" fontSize="8" color="#333333"/>
      </tickLabels>
    </categoryAxis>

    <primaryValueAxis visible="1">
      <title show="1"><text><![CDATA[$M]]></text></title>
      <bounds minimum="0" maximum="200" autoMinimum="0" autoMaximum="1"/>
      <units major="50" autoMajor="1"/>
      <majorGridlines show="1" color="#D9D9D9" width="0.5" dashStyle="solid"/>
      <majorTickMark v="none"/>
      <tickLabels show="1" formatCode="#,##0" position="nextTo"/>
    </primaryValueAxis>

    <chartArea>
      <fill/>
      <outline/>
    </chartArea>

    <plotArea>
      <fill/>
      <outline/>
      <layout x="0.1" y="0.12" width="0.85" height="0.7"/>      <!-- [EDIT] fractional 0-1 -->
      <actualLayout x="62" y="42" width="527" height="245"/>      <!-- [RO] points -->
    </plotArea>

    <barColumnOptions gapWidth="150" overlap="0"/>
  </chartInfo>
</shape>
\`\`\`

Chart types: \`columnClustered\`, \`columnStacked\`, \`columnStacked100\`, \`barClustered\`, \`barStacked\`, \`line\`, \`lineMarkers\`, \`pie\`, \`pieExploded\`, \`doughnut\`, \`area\`, \`areaStacked\`, \`scatter\`, \`scatterLines\`, \`bubble\`, \`radar\`, \`stock\`, \`waterfall\`, \`funnel\`, \`treemap\`, \`sunburst\`, \`histogram\`, \`pareto\`, \`boxWhisker\` (and 3D variants)

Data modes: \`raw\` (all values), \`summary\` (statistics only), \`unavailable\` (external workbook)

Legend positions: \`bottom\`, \`top\`, \`left\`, \`right\`, \`topRight\`
Data label positions: \`center\`, \`insideEnd\`, \`insideBase\`, \`outsideEnd\`, \`left\`, \`right\`, \`top\`, \`bottom\`, \`bestFit\`
Tick mark types: \`none\`, \`inside\`, \`outside\`, \`cross\`
Tick label positions: \`high\`, \`low\`, \`nextTo\`, \`none\`
Display units: \`none\`, \`hundreds\`, \`thousands\`, \`millions\`, \`billions\`, \`trillions\`

**Editable chart styling:**
- Title: show/hide, text, position (overlay/above), textFormat, **layout** (fractional x/y/w/h 0-1)
- Legend: show/hide, position, overlay, textFormat, **layout** (fractional x/y/w/h 0-1)
- Series styling: fill (color, transparency), outline (color, width, dashStyle, transparency), line (color, width, dashStyle, transparency), marker (style, size), smooth, explosion
- **Per-point overrides**: fill (color, transparency), outline (color, width, dashStyle, transparency), marker (style, size, fill, outline), explosion, dataLabel (show, position, formatCode, text, textFormat) — edit \`<point pointIndex="N">\` elements inside \`<dataPoints>\`
- Data labels (series-level): show/hide, position, formatCode, showValue/CategoryName/SeriesName/Percentage, separator, textFormat
- Axes: bounds (min/max, auto), units (major/minor, auto), title, gridlines (show, color, width, dashStyle, transparency), axis line (visible, color, width, dashStyle, transparency), tick marks, tick label format
- Chart area: fill (color/transparency), outline (color/width/dashStyle/transparency)
- Plot area: fill (color/transparency), outline (color/width/dashStyle/transparency), **layout** (fractional x/y/w/h 0-1)
- Bar/column: gapWidth, overlap
- Pie: rotation, holeSize

Ref support in chart styling:
- \`<textFormat ref="fnX"/>\` and \`<labelTextFormat ref="fnX"/>\` resolve font defs (inline attrs override ref attrs).
- \`<line ref="oX"/>\` resolves outline defs for chart lines (series/axis/trendline/error bars).

**Per-point styling** lets you color individual bars, pie slices, or markers differently from the series default. Edit the \`<fill>\`, \`<outline>\`, \`<marker>\`, or \`<dataLabel>\` inside a \`<point pointIndex="N">\` element. Points without overrides inherit the series default.

Layout values are **fractional 0.0-1.0** relative to chart dimensions. E.g. \`<layout x="0.3" y="0.02" width="0.4" height="0.08"/>\` positions at 30% from left, 2% from top.

**Editable chart data:**
- Categories: change \`<value v="..."/>\` inside \`<categories>\`
- Series values: change \`<value v="..."/>\` inside \`<values>\` within a \`<series>\`
- Series name: change \`name="..."\` attribute on \`<series>\`
- Add/remove series: add/remove entire \`<series>\` blocks (update indices)

Data edits open the embedded Excel workbook, write cells, resize Table1, and close. This takes ~1-2s extra.

**Read-only:** \`actualLayout\` (computed points), \`pointPositions\`, \`dataMode\`, \`seriesCount\`.

---

### Chart Summary Mode (large datasets)

\`\`\`xml
<series index="0" mode="summary" name="Revenue">
  <statistics count="100" validCount="98" min="12.5" max="450.2"
              sum="15230" mean="155.4" stdDev="82.1" median="143"
              q25="95" q75="210"/>
  <trend direction="increasing" changePct="15.2" firstValue="120" lastValue="180"/>
  <firstValues><value v="120"/><value v="125"/><value v="130"/></firstValues>
  <lastValues><value v="175"/><value v="178"/><value v="180"/></lastValues>
  <nullCount v="2"/>
</series>
\`\`\`

---

### Chart Trendlines & Error Bars [RO]

\`\`\`xml
<trendlines>
  <trendline type="linear" name="Linear Trend" displayEquation="1" displayRSquared="1">
    <line visible="1" color="#FF0000" width="1.5"/>
  </trendline>
</trendlines>

<errorBarsY type="percentage" value="5" direction="both" endStyle="cap">
  <line visible="1" color="#000000" width="0.75"/>
</errorBarsY>
\`\`\`

Trendline types: \`linear\`, \`exponential\`, \`logarithmic\`, \`polynomial\`, \`power\`, \`movingAverage\`
Error bar types: \`fixedValue\`, \`percentage\`, \`standardDeviation\`, \`standardError\`, \`custom\`

---

### Chart Point Positions [RO]

Computed data-point positions for callout placement. Chart-relative coordinates in pts.
Add \`shape.position.x/y\` to get slide-absolute coordinates.

\`\`\`xml
<pointPositions readOnly="1">
  <series si="0" name="Americas">
    <pt i="0" cat="Q1" val="120">
      <anchor x="95.5" y="180.2"/>
      <bounds x="70" y="180.2" w="50" h="65"/>
    </pt>
    <pt i="1" cat="Q2" val="135">
      <anchor x="195.5" y="155.8"/>
      <bounds x="170" y="155.8" w="50" h="89.2"/>
    </pt>
  </series>
</pointPositions>
\`\`\`

Anchor semantics by chart type:
- Column (clustered): top center of bar
- Column (stacked): top center of stacked segment
- Bar (clustered): right center of bar
- Pie/Doughnut: midpoint of outer arc (\`startAngle\`/\`endAngle\` included)
- Line/Area/Scatter: vertex/marker position (no bounds)

---

### Slide Background [EDIT]

Edit the \`<background>\` block inside the \`<layout>\` section:
\`\`\`xml
<!-- Set solid background -->
<background>
  <fill type="solid" color="#F0F4F8"/>
</background>

<!-- Revert to master background: remove the <background> block -->
\`\`\`

### Speaker Notes [EDIT]

Edit the \`<notes>\` block inside the \`<layout>\` section:
\`\`\`xml
<notes><![CDATA[Key talking points for this slide...]]></notes>
\`\`\`

### Layout & Master Shapes [EDIT — affects all slides using same layout/master]

Shapes from the \`<layout>\` section (IDs like \`masterShape_50\`, \`layoutShape_7\`) CAN be edited.
Same editing rules apply (position, fill, outline, text, etc.).

**WARNING:** Editing master/layout shapes changes them for ALL slides using that master/layout.
This is useful for updating logos, headers, footers, and other template elements globally.

### FYI Section [RO — DO NOT include in changes]

\`\`\`xml
<fyi>
  <whitespace percent="54.3" regions="20">
    <region x="0" y="0" w="720" h="36.21" area="26071.2"/>
    <region x="40.1" y="145.42" w="75.77" h="335.57" area="25426.14"/>
  </whitespace>
  <overlaps>
    <o shape="5" other="10" otherKind="shape" otherType="picture" z="above" area="2709.36"/>
  </overlaps>
</fyi>
\`\`\`

---

### Currently Supported Writes

| Change | What to modify | Example |
|--------|---------------|---------|
| **Text** | CDATA inside \`<run>\` | \`<![CDATA[Old]]>\` → \`<![CDATA[New]]>\` |
| **Position** | \`<position/>\` attrs | Change x/y/width/height |
| **Fill** | \`<fill/>\` tag | \`<fill ref="f1"/>\` → \`<fill/>\` |
| **Outline** | \`<outline/>\` tag | \`<outline/>\` → \`<outline color="#C6EFFF" width="1.5" dashStyle="dash" transparency="0.2"/>\` |
| **TextFrame** | \`<textFrame/>\` attrs or \`ref\` | Change margins, verticalAnchor, autofit, wordWrap, textDirection |
| **Paragraph** | \`<p>\` attrs | alignment, spacing, indent, marginLeft, level, bullets (type/char/color/size/font/numberFormat/startAt) |
| **Font/Run** | \`<run>\` attrs | Change font ref or add inline bold, italic, color, size, etc. |
| **Hyperlink** | \`<link/>\` tag | Add/remove \`<link url="..." tooltip="..."/>\` after a run |
| **Rotation** | shape attr | \`rotation="45"\` on the \`<shape>\` tag |
| **Hidden** | shape attr | \`hidden="1"\` on the \`<shape>\` tag |
| **Geometry type** | \`presetGeometry\` attr | Change shape type (e.g. RECTANGLE → ELLIPSE) |
| **Geometry adj** | \`<adjustments/>\` | Change adj1, adj2 values |
| **Image crop** | \`<crop/>\` in imageInfo | Set left/right/top/bottom (0–1 fractions) |
| **Shadow** | \`<effects><shadow/>\` | Add/remove/modify shadow properties |
| **Highlight** | run attr | \`highlightColor="#FFFF00"\` on a \`<run>\` tag |
| **Table cells** | \`<cell>\` content | Cell text, fill (+ transparency), borders incl diagonal (+ transparency), margins, vertical align, textFrame, textDirection (attr/tag compatible) |
| **Table dims** | widths/heights | Change \`<columnWidth>\` and \`<rowHeight>\` values |
| **Table struct** | add/remove | Add/remove \`<rowHeight>\`/\`<columnWidth>\` entries |
| **Table merges** | cell span attrs | Change \`rowSpan\`/\`colSpan\` and runtime will split/merge cells |
| **Table style flags** | \`<tableInfo ...>\` attrs | Set \`tableStyleId\`, \`firstRow\`, \`lastRow\`, \`firstColumn\`, \`lastColumn\`, \`bandedRows\`, \`bandedColumns\` |
| **Connector pts** | \`<point>\` tags | Move, add, or remove through-points |
| **Connector line** | \`<line>\` in connectorInfo | Color, weight, dashStyle, transparency |
| **Arrowheads** | \`<arrowheadStart/End>\` | Style, length, width |
| **Chart styling** | \`<chartInfo>\` content | Title, legend, series fill/line (+ transparency, incl \`line ref\`), **per-point fills/outlines/markers**, data labels (incl \`textFormat ref\`), axes (incl axis-line transparency), gridlines |
| **Chart options** | chart-type blocks | gapWidth, overlap, rotation, holeSize |
| **Chart areas** | \`<chartArea>\`/\`<plotArea>\` | Fill/outline on chart/plot area (incl transparency) |
| **Chart data** | \`<categories>\`/\`<values>\` | Category labels, series values, series names |
| **Metadata** | \`<carouselMetadata>\` | Attach/detach entries and update \`mapsTo\` targeting |
| **Image fill** | \`<fill type="picture" contextId="logo_*"/>\` | Set image from ContextResolver |
| **Background** | \`<background>\` in layout | Slide background fill |
| **Notes** | \`<notes>\` in layout | Speaker notes text |
| **Master/layout** | shapes with masterShape_*/layoutShape_* IDs | Edit inherited shapes (affects all slides) |
| **Delete** | replace = \`""\` | Remove entire \`<shape>...</shape>\` |
| **Create shape** | \`shapeId="new_1"\` + \`type\`/geometry attrs | \`type="textBox"\` → AddTextbox (default insets 0). \`type="shape"\` + \`presetGeometry="RECTANGLE"\` → AddShape rectangle (PowerPoint default insets). |
| **Create table** | \`shapeId="new_1" type="table"\` | Full table with cells, borders, column widths, row heights |
| **Create connector** | \`shapeId="new_1" type="connector"\` | Straight, elbow, or curved connectors with line styling and arrowheads |
| **Create picture** | \`shapeId="new_1" type="picture"\` | Image from contextId (logo_*, wikimedia_*, artifact_*) |
| **Create excel artifact** | \`shapeId="new_1" type="excelArtifact"\` | Insert Excel artifact from \`artifact_*\` as image or OLE |
| **Replace with excel artifact** | \`<excelArtifact contextId="artifact_*" mode="..."/>\` | Replace existing shape with artifact at same position |
| **Create chart** | \`shapeId="new_1" type="chart"\` | Chart with data, series styling, title, legend, axes |
| **Group shapes** | See grouping section below | Group existing shapes together (cannot create groups directly) |

All major table parity features implemented, including merge editing, style-flag writes, and diagonal borders.

---

### writeSlide Parameters

\`\`\`json
{
  "slideId": "slide_318",
  "slideIndex": 10,
  "detailLevel": "complete",
  "reReadAfterWrite": true,
  "computeSpatialDelta": true,
  "verbose": true,
  "changes": [{
    "search": "exact XML from readSlide",
    "replace": "modified XML (or empty string to delete)"
  }]
}
\`\`\`

- \`reReadAfterWrite\` (default \`true\`): set to \`false\` for faster writes when you do not need immediate \`updatedXml\`.
- \`computeSpatialDelta\` (default \`true\`): set to \`false\` when overlap/off-slide/whitespace diagnostics are not needed.
- \`verbose\` (default \`true\`): backend logging verbosity toggle.

### Streaming-Friendly Emission Rules (IMPORTANT)

When constructing \`writeSlide\` arguments, optimize for incremental streaming safety:

0. Prefer **one \`writeSlide\` tool call per user request**. Put all intended edits in that call's \`changes\` array.
1. Emit \`changes\` as **small, manageable blocks** (prefer 1-3 blocks at a time), not one huge monolithic block.
2. Each \`changes[i]\` entry must be a **complete JSON object** with both:
   - \`"search"\` as a fully closed string
   - \`"replace"\` as a fully closed string (or \`""\` for delete)
3. Keep each block **self-contained** and shape-scoped whenever possible (e.g., one shape edit per block).
4. Do **not** emit partial/incomplete strings for a block. If a block is large, split into multiple smaller search/replace edits.
5. Prefer many valid small edits over a single giant edit to improve first-apply latency and reduce malformed JSON risk.
6. **Keep search strings minimal** — include only the XML fragment being changed plus enough context to make it unique. \`shapeId\` is already unique, so do NOT copy entire shape blocks with all paragraphs just to change a position or fill.
   - Position change: search for \`<shape shapeId="X" ...>\\n  <position .../>\`, not the entire shape
   - Text change: search for the specific \`<run>...</run>\` or \`<p>...</p>\`, not the whole shape
   - Fill/outline change: search for \`<fill .../>\`, include parent shape tag only if not unique
7. Exception: some create operations (e.g., full table/chart XML) may require one large block. In those cases, keep that single block complete and valid, then stream follow-up styling/position tweaks as separate smaller blocks.

### Tool Call Strategy (IMPORTANT)

- Default to **a single \`writeSlide\` call with multiple \`changes\` entries**.
- Do **not** split into multiple \`writeSlide\` calls just to stream tokens.
- **After any writeSlide, you MUST call readSlide before making further edits.** The XML changes after every write — search strings from a previous read will not match.
- Do NOT retry failed changes without re-reading first. If a search string failed, the XML has changed.
- **Stop after 2 fix attempts.** If a change fails twice after re-reading, skip it and move on — do not loop endlessly on the same edit.
- If multiple calls are unavoidable, keep call count minimal and explain the dependency in reasoning.

### Performance Hints

- Prefer one streamed write sequence (\`start\` -> \`append\` -> \`commit\`) for iterative edit batches.
- Use the lowest practical \`detailLevel\` (\`layout\`/\`complete\`) for your operation.
- For heavy table styling, prefer table-specialized flows (\`createTable\` + \`copyTableStyle\`) over many manual cell edits.


### Examples

**Remove fill, add outline:**
\`\`\`json
{
  "changes": [
    {
      "search": "<fill ref=\\"f1\\"/>\\n      <outline/>",
      "replace": "<fill/>\\n      <outline color=\\"#C6EFFF\\" width=\\"1.5\\"/>"
    }
  ]
}
\`\`\`

**Change text:**
\`\`\`json
{
  "changes": [
    {
      "search": "<run font=\\"fn1\\"><![CDATA[Old Title]]></run>",
      "replace": "<run font=\\"fn1\\"><![CDATA[New Title]]></run>"
    }
  ]
}
\`\`\`

**Make text bold and red (inline font override):**
\`\`\`json
{
  "changes": [
    {
      "search": "<run font=\\"fn1\\"><![CDATA[Important]]></run>",
      "replace": "<run font=\\"fn1\\" bold=\\"1\\" color=\\"#FF0000\\"><![CDATA[Important]]></run>"
    }
  ]
}
\`\`\`

**Change paragraph alignment to center (preferred — edit paraStyle):**
\`\`\`json
{
  "changes": [
    {
      "search": "<paraStyle id=\\"p1\\" alignment=\\"left\\" level=\\"0\\"/>",
      "replace": "<paraStyle id=\\"p1\\" alignment=\\"center\\" level=\\"0\\"/>"
    }
  ]
}
\`\`\`

**Add bullet to paragraph (edit paraStyle):**
\`\`\`json
{
  "changes": [
    {
      "search": "<paraStyle id=\\"p1\\" alignment=\\"left\\" level=\\"0\\"/>",
      "replace": "<paraStyle id=\\"p1\\" alignment=\\"left\\" level=\\"0\\" marginLeft=\\"13.5\\" indent=\\"-13.5\\" bulletType=\\"char\\" bulletChar=\\"•\\"/>"
    }
  ]
}
\`\`\`

**Add numbered list (edit paraStyle):**
\`\`\`json
{
  "changes": [
    {
      "search": "<paraStyle id=\\"p1\\" alignment=\\"left\\" level=\\"0\\"/>",
      "replace": "<paraStyle id=\\"p1\\" alignment=\\"left\\" level=\\"0\\" marginLeft=\\"18\\" indent=\\"-18\\" bulletType=\\"autoNumber\\" numberFormat=\\"arabicPeriod\\"/>"
    }
  ]
}
\`\`\`

**Change textFrame vertical anchor:**
\`\`\`json
{
  "changes": [
    {
      "search": "<textFrame marginLeft=\\"5\\" marginTop=\\"3\\" marginRight=\\"5\\" marginBottom=\\"3\\" wordWrap=\\"1\\" verticalAnchor=\\"top\\" autofit=\\"none\\"/>",
      "replace": "<textFrame marginLeft=\\"5\\" marginTop=\\"3\\" marginRight=\\"5\\" marginBottom=\\"3\\" wordWrap=\\"1\\" verticalAnchor=\\"middle\\" autofit=\\"shrink\\"/>"
    }
  ]
}
\`\`\`

**Add shadow:**
\`\`\`json
{
  "changes": [
    {
      "search": "<outline/>\\n  <geometry",
      "replace": "<outline/>\\n  <effects>\\n    <shadow type=\\"outer\\" color=\\"#000000\\" blurRadius=\\"4\\" distance=\\"2\\" angle=\\"315\\" transparency=\\"0.5\\"/>\\n  </effects>\\n  <geometry"
    }
  ]
}
\`\`\`

**Move + resize:**
\`\`\`json
{
  "changes": [
    {
      "search": "<position x=\\"100\\" y=\\"200\\" width=\\"300\\" height=\\"50\\"/>",
      "replace": "<position x=\\"150\\" y=\\"200\\" width=\\"350\\" height=\\"60\\"/>"
    }
  ]
}
\`\`\`

**Add hyperlink to text:**
\`\`\`json
{
  "changes": [
    {
      "search": "<run font=\\"fn1\\"><![CDATA[Click here]]></run>",
      "replace": "<run font=\\"fn1\\"><![CDATA[Click here]]></run>\\n  <link url=\\"https://example.com\\" tooltip=\\"Visit\\"/>"
    }
  ]
}
\`\`\`

**Delete shape:** set replace to \`""\` with the full \`<shape>...</shape>\` block as search.

**Create rectangle shape (AddShape default insets, logical blocks):**
\`\`\`json
{
  "changes": [
    {
      "search": "</content>",
      "replace": "<shape shapeId=\\"new_1\\" name=\\"Label\\" type=\\"shape\\">\\n  <position x=\\"100\\" y=\\"100\\" width=\\"200\\" height=\\"40\\"/>\\n  <fill/>\\n  <outline/>\\n  <p level=\\"0\\" alignment=\\"left\\">\\n    <run font=\\"fn1\\"><![CDATA[New text]]></run>\\n  </p>\\n  <geometry presetGeometry=\\"RECTANGLE\\"/>\\n</shape>\\n</content>"
    },
    {
      "search": "<position x=\\"100\\" y=\\"100\\" width=\\"200\\" height=\\"40\\"/>",
      "replace": "<position x=\\"120\\" y=\\"96\\" width=\\"260\\" height=\\"48\\"/>"
    },
    {
      "search": "<fill/>\\n  <outline/>",
      "replace": "<fill type=\\"solid\\" color=\\"#F3F7FF\\"/>\\n  <outline color=\\"#1F4E79\\" width=\\"1\\"/>"
    }
  ]
}
\`\`\`

**Create true textbox (AddTextbox, missing margins default to 0):**
\`\`\`json
{
  "changes": [
    {
      "search": "</content>",
      "replace": "<shape shapeId=\\"new_2\\" name=\\"Textbox 1\\" type=\\"textBox\\">\\n  <position x=\\"120\\" y=\\"160\\" width=\\"260\\" height=\\"48\\"/>\\n  <fill/>\\n  <outline/>\\n  <p level=\\"0\\" alignment=\\"left\\">\\n    <run font=\\"fn1\\"><![CDATA[Textbox text with 0 default inset unless textFrame margins are provided]]></run>\\n  </p>\\n</shape>\\n</content>"
    }
  ]
}
\`\`\`

**Create table (logical blocks):**
\`\`\`json
{
  "changes": [
    {
      "search": "</content>",
      "replace": "<shape shapeId=\\"new_1\\" name=\\"Table 1\\" type=\\"table\\">\\n  <position x=\\"50\\" y=\\"100\\" width=\\"620\\" height=\\"200\\"/>\\n  <fill/>\\n  <outline/>\\n  <tableInfo rows=\\"2\\" columns=\\"3\\">\\n    <columnWidths><columnWidth v=\\"200\\"/><columnWidth v=\\"200\\"/><columnWidth v=\\"220\\"/></columnWidths>\\n    <rowHeights><rowHeight v=\\"25\\"/><rowHeight v=\\"30\\"/></rowHeights>\\n    <cells>\\n      <cell row=\\"1\\" column=\\"1\\" rowSpan=\\"1\\" colSpan=\\"1\\"><paragraphs><p level=\\"0\\"><run font=\\"fn2\\"><![CDATA[Col A]]></run></p></paragraphs></cell>\\n      <cell row=\\"1\\" column=\\"2\\" rowSpan=\\"1\\" colSpan=\\"1\\"><paragraphs><p level=\\"0\\"><run font=\\"fn2\\"><![CDATA[Col B]]></run></p></paragraphs></cell>\\n      <cell row=\\"1\\" column=\\"3\\" rowSpan=\\"1\\" colSpan=\\"1\\"><paragraphs><p level=\\"0\\"><run font=\\"fn2\\"><![CDATA[Col C]]></run></p></paragraphs></cell>\\n      <cell row=\\"2\\" column=\\"1\\" rowSpan=\\"1\\" colSpan=\\"1\\"><paragraphs><p level=\\"0\\"><run font=\\"fn1\\"><![CDATA[Val A]]></run></p></paragraphs></cell>\\n      <cell row=\\"2\\" column=\\"2\\" rowSpan=\\"1\\" colSpan=\\"1\\"><paragraphs><p level=\\"0\\"><run font=\\"fn1\\"><![CDATA[Val B]]></run></p></paragraphs></cell>\\n      <cell row=\\"2\\" column=\\"3\\" rowSpan=\\"1\\" colSpan=\\"1\\"><paragraphs><p level=\\"0\\"><run font=\\"fn1\\"><![CDATA[Val C]]></run></p></paragraphs></cell>\\n    </cells>\\n  </tableInfo>\\n</shape>\\n</content>"
    },
    {
      "search": "<position x=\\"50\\" y=\\"100\\" width=\\"620\\" height=\\"200\\"/>",
      "replace": "<position x=\\"48\\" y=\\"96\\" width=\\"624\\" height=\\"210\\"/>"
    },
    {
      "search": "<cell row=\\"1\\" column=\\"1\\" rowSpan=\\"1\\" colSpan=\\"1\\"><paragraphs><p level=\\"0\\"><run font=\\"fn2\\"><![CDATA[Col A]]></run></p></paragraphs></cell>",
      "replace": "<cell row=\\"1\\" column=\\"1\\" rowSpan=\\"1\\" colSpan=\\"1\\"><fill type=\\"solid\\" color=\\"#EAF3FF\\"/><paragraphs><p level=\\"0\\" alignment=\\"center\\"><run font=\\"fn2\\"><![CDATA[Investor]]></run></p></paragraphs></cell>"
    }
  ]
}
\`\`\`

**Create connector (logical blocks):**
\`\`\`json
{
  "changes": [
    {
      "search": "</content>",
      "replace": "<shape shapeId=\\"new_1\\" name=\\"Line 1\\" type=\\"connector\\">\\n  <position x=\\"50\\" y=\\"200\\" width=\\"620\\" height=\\"0\\"/>\\n  <fill/>\\n  <outline/>\\n  <connectorInfo connectorType=\\"straight\\">\\n    <points><point x=\\"50\\" y=\\"200\\"/><point x=\\"670\\" y=\\"200\\"/></points>\\n    <line color=\\"#A9B1B0\\" width=\\"0.75\\"/>\\n  </connectorInfo>\\n</shape>\\n</content>"
    },
    {
      "search": "<outline/>",
      "replace": "<outline color=\\"#1F4E79\\" width=\\"1.25\\" dashStyle=\\"dash\\"/>"
    }
  ]
}
\`\`\`

**Create picture (logical blocks, with contextId from imageSearch):**
\`\`\`json
{
  "changes": [
    {
      "search": "</content>",
      "replace": "<shape shapeId=\\"new_1\\" name=\\"Logo\\" type=\\"picture\\">\\n  <position x=\\"600\\" y=\\"30\\" width=\\"80\\" height=\\"30\\"/>\\n  <fill type=\\"picture\\" contextId=\\"logo_abc123\\"/>\\n  <outline/>\\n</shape>\\n</content>"
    },
    {
      "search": "<position x=\\"600\\" y=\\"30\\" width=\\"80\\" height=\\"30\\"/>",
      "replace": "<position x=\\"590\\" y=\\"26\\" width=\\"92\\" height=\\"34\\"/>"
    }
  ]
}
\`\`\`

**Create Excel artifact (image mode):**
\`\`\`json
{ "search": "</content>",
  "replace": "<shape shapeId=\\"new_1\\" name=\\"Excel Artifact\\" type=\\"excelArtifact\\">\\n  <position x=\\"80\\" y=\\"110\\" width=\\"560\\" height=\\"300\\"/>\\n  <excelArtifact contextId=\\"artifact_abc123\\" mode=\\"image\\"/>\\n</shape>\\n</content>" }
\`\`\`

**Create Excel artifact (OLE mode):**
\`\`\`json
{ "search": "</content>",
  "replace": "<shape shapeId=\\"new_1\\" name=\\"Excel OLE Artifact\\" type=\\"excelArtifact\\">\\n  <position x=\\"80\\" y=\\"110\\" width=\\"560\\" height=\\"300\\"/>\\n  <excelArtifact contextId=\\"artifact_abc123\\" mode=\\"ole\\"/>\\n</shape>\\n</content>" }
\`\`\`

**Replace an existing shape with an Excel artifact:**
\`\`\`json
{ "search": "<shape shapeId=\\"42\\" name=\\"Placeholder\\" type=\\"shape\\">\\n  <position x=\\"80\\" y=\\"110\\" width=\\"560\\" height=\\"300\\"/>\\n  <fill/>\\n  <outline/>\\n</shape>",
  "replace": "<shape shapeId=\\"42\\" name=\\"Placeholder\\" type=\\"shape\\">\\n  <position x=\\"80\\" y=\\"110\\" width=\\"560\\" height=\\"300\\"/>\\n  <excelArtifact contextId=\\"artifact_abc123\\" mode=\\"ole\\"/>\\n</shape>" }
\`\`\`

**Create chart (logical blocks):**
\`\`\`json
{
  "changes": [
    {
      "search": "</content>",
      "replace": "<shape shapeId=\\"new_1\\" name=\\"Chart 1\\" type=\\"chart\\">\\n  <position x=\\"50\\" y=\\"100\\" width=\\"620\\" height=\\"350\\"/>\\n  <fill/>\\n  <outline/>\\n  <chartInfo chartType=\\"columnClustered\\" seriesCount=\\"2\\">\\n    <title show=\\"1\\"><text><![CDATA[Revenue]]></text></title>\\n    <legend show=\\"1\\" position=\\"bottom\\"/>\\n    <categories><value v=\\"Q1\\"/><value v=\\"Q2\\"/><value v=\\"Q3\\"/></categories>\\n    <series index=\\"0\\" name=\\"Americas\\"><values><value v=\\"120\\"/><value v=\\"135\\"/><value v=\\"150\\"/></values><fill type=\\"solid\\" color=\\"#4472C4\\"/></series>\\n    <series index=\\"1\\" name=\\"EMEA\\"><values><value v=\\"80\\"/><value v=\\"95\\"/><value v=\\"88\\"/></values><fill type=\\"solid\\" color=\\"#ED7D31\\"/></series>\\n  </chartInfo>\\n</shape>\\n</content>"
    },
    {
      "search": "<title show=\\"1\\"><text><![CDATA[Revenue]]></text></title>",
      "replace": "<title show=\\"1\\"><text><![CDATA[Revenue by Region (USD M)]]></text></title>"
    },
    {
      "search": "<legend show=\\"1\\" position=\\"bottom\\"/>",
      "replace": "<legend show=\\"1\\" position=\\"right\\"/>"
    }
  ]
}
\`\`\`

---

### Grouping Shapes

You **cannot** create a group directly with \`shapeId="new_1" type="group"\`.
Current orchestrator runtime does not expose a direct grouping tool.
Use this approach:

1. Create or identify the shapes you want grouped (they must already exist).
2. Keep them ungrouped in automated flow, or perform manual grouping in PowerPoint when required.

**Why?** COM grouping requires selecting a range of shape objects (\`Shapes.Range([ids]).Group()\`),
which is a multi-shape operation that doesn't fit the single-shape search/replace model.

---

### Pitfalls

1. **Prefer canonical no-fill/no-outline forms** — use \`<fill/>\` and \`<outline/>\` for stable round-trips (runtime still accepts a few legacy variants)
2. **Cell compat forms are accepted, but be explicit when possible** — \`verticalAlign\` / \`textDirection\` can be attrs or child tags; \`textFrame\` verticalAnchor wins over cell \`verticalAlign\`
3. **search must match exactly** — copy verbatim from readSlide, whitespace matters
4. **Include enough context** — use surrounding shape tag if fragment is not unique
5. **Don't edit RO fields** — textFrameDimensions, actualTextBounds are auto-recomputed
6. **Defs edits can be global** — modifying referenced \`font\`/\`paraStyle\`/\`fill\`/\`outline\`/\`effect\`/\`tableBorder\`/\`textFrame\` defs updates all shapes/cells/charts/connectors that reference those IDs
7. **Layout/master shapes are global** — editing \`masterShape_*\`/\`layoutShape_*\` affects ALL slides using that master/layout
8. **Don't include \`<fyi>\` in changes** — informational only
9. **Styled tables:** when \`tableStyleId\` exists, avoid explicit \`<borders>\` on inserted cells unless custom borders are requested
10. **Preserve per-run formatting when replacing text** — if the original has multiple runs with different font refs, keep those same refs in your replacement. Only change a font ref if you intentionally want to change that run's formatting.
11. **Excel artifacts require context payload** — \`artifact_*\` must resolve in session context
12. **OLE mode needs Windows COM** — if unavailable, use \`mode="image"\`
`;
